import { forwardRef } from 'react';
import { Menu } from '@mui/material';

export type Column =
  | 'sonioId'
  | 'referenceId'
  | 'flowName'
  | 'fullName'
  | 'firstName'
  | 'lastName'
  | 'dateCreated'
  | 'dateReviewed'
  | 'status'
  | 'result'
  | 'assign';

interface TableViewModalProps {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  selectedColumns: Column[];
  onColumnChange: (columns: Column[]) => void;
}

const columnOptions = [
  { key: 'sonioId', label: 'SONIO ID' },
  { key: 'referenceId', label: 'Reference ID' },
  { key: 'flowName', label: 'Flow Name' },
  { key: 'fullName', label: 'Full Name' },
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'dateCreated', label: 'Time Frame' },
  { key: 'dateReviewed', label: 'Date Reviewed' },
  { key: 'status', label: 'Status' },
  { key: 'result', label: 'Result' },
  { key: 'assign', label: 'Assign' },
] as const;

const TableViewModal = forwardRef<HTMLDivElement, TableViewModalProps>(
  ({ open, onClose, anchorEl, selectedColumns, onColumnChange }, ref) => {
    const handleColumnToggle = (columnKey: Column, checked: boolean) => {
      if (checked) {
        onColumnChange(Array.from(new Set([...selectedColumns, columnKey])));
      } else {
        onColumnChange(selectedColumns.filter(k => k !== columnKey));
      }
    };

    return (
      <Menu
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 320 } }}
      >
        <div ref={ref}>
          <div className="px-3 py-2 border-b">
            <span className="text-sm font-semibold">Table View</span>
          </div>
          <div className="p-3 space-y-1 max-h-80 overflow-auto">
            <div className="text-xs font-semibold text-[#5B6B80] px-2 py-1">
              Columns
            </div>
            {columnOptions.map(col => (
              <label
                key={col.key}
                className="flex items-center gap-2 px-2 py-1 text-sm cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedColumns.includes(col.key as Column)}
                  onChange={e => {
                    handleColumnToggle(col.key as Column, e.target.checked);
                  }}
                />
                {col.label}
              </label>
            ))}
          </div>
        </div>
      </Menu>
    );
  },
);

TableViewModal.displayName = 'TableViewModal';

export default TableViewModal;
export type { TableViewModalProps };
